import { memo, useCallback, useEffect, useState } from 'react'

import { CloudUpload } from '@mui/icons-material'
import { Button, CircularProgress, Typography } from '@mui/material'

import { useEditorContext } from '@shared/components/Editor'
import { Kbd } from '@shared/components/MaterialUIEnhancements'
import { currentOS, OS } from '@shared/utils/common'

export type SubmitFn = (callbacks: { onSuccess: () => void; onError: () => void }) => void

export type SubmitButtonProps = {
  onSubmit?: SubmitFn
  readOnly?: boolean
  readOnlyLabel?: string
  inProgress?: boolean
}

const SubmitButtonRaw = ({ readOnly, inProgress, onSubmit, readOnlyLabel }: SubmitButtonProps) => {
  const { parserError } = useEditorContext()

  const [visualState, setVisualState] = useState<'success' | 'error'>()

  const handleSubmit = useCallback(() => {
    if (onSubmit && !parserError) {
      onSubmit({ onSuccess: () => setVisualState('success'), onError: () => setVisualState('error') })
    }
  }, [onSubmit, parserError])

  useEffect(() => {
    if (visualState !== undefined) {
      setTimeout(() => {
        setVisualState(undefined)
      }, 2000)
    }
  }, [visualState])

  if (readOnly) {
    return <Kbd>{readOnlyLabel || 'Read-only'}</Kbd>
  }

  return (
    <>
      <Button
        sx={{ textTransform: 'unset' }}
        variant="contained"
        color={!visualState ? 'primary' : visualState}
        endIcon={inProgress ? <CircularProgress sx={{ color: 'white.main' }} size="1em" /> : <CloudUpload />}
        onClick={handleSubmit}
        disabled={!!inProgress || parserError}
      >
        <Typography sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5 }} variant="heading-sm">
          Submit
        </Typography>
      </Button>
      <Kbd sx={{ color: (theme) => theme.palette.text.primary }}>{currentOS === OS.MAC_OS ? '⌘' : 'Ctrl'}+S</Kbd>
    </>
  )
}

export const SubmitButton = memo(SubmitButtonRaw)
